import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useCatalogItems, isItemModified, CATALOG_MAP } from '../../../lib/catalogItems'
import { useSubServices, buildSubServiceMap } from '../../../lib/subServices'
import { useCategories, getCatColor, COLOR_CLASSES } from '../../../lib/categories'
import {
    FaLock, FaEdit, FaPlus, FaTrash, FaSearch, FaChevronUp,
    FaCheckCircle, FaTimesCircle, FaSave, FaSync, FaCopy, FaMoneyBillWave, FaTimes, FaDownload
} from 'react-icons/fa'
import { usePriceGroups } from '../../../lib/priceGroups'
import { useUnits } from '../../../lib/useUnits'
import { exportTwoSheet, exportFlatSheet, exportHorizontalSheet, downloadWorkbook, getExportFilename } from '../../../lib/catalogExport'

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// ── SubServicePicker ──────────────────────────────────────────────────────────
// Manages inc_ids / exc_ids (UUID arrays) with resolved text display.
function SubServicePicker({ ids, type, itemCat, subServices, subServiceMap, onIdsChange, oppositeIds }) {
    const [input,    setInput]    = useState('')
    const [showDrop, setShowDrop] = useState(false)
    const [dropPos,  setDropPos]  = useState(null)
    const inputRef = useRef(null)

    const computeDropPos = useCallback(() => {
        if (inputRef.current) {
            const r = inputRef.current.getBoundingClientRect()
            setDropPos({ top: r.bottom + 4, left: r.left, width: r.width })
        }
    }, [])

    const { t } = useTranslation()
    const color     = type === 'inc' ? 'text-emerald-600' : 'text-red-500'
    const pillClass = type === 'inc' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'
    const label     = type === 'inc' ? t('admin.catalog.included') : t('admin.catalog.excluded')

    const suggestions = useMemo(() => {
        const q = input.trim().toLowerCase()
        return subServices
            .filter(s => {
                if (ids.includes(s.id)) return false
                if (oppositeIds?.includes(s.id)) return false
                if (!q) return !s.cat || s.cat === itemCat
                return s.text.toLowerCase().includes(q)
            })
            .slice(0, 8)
    }, [input, subServices, ids, oppositeIds, itemCat])

    const select = (id) => {
        if (!ids.includes(id)) onIdsChange([...ids, id], null)
        setInput('')
        setShowDrop(false)
    }

    const remove = (id) => onIdsChange(ids.filter(i => i !== id), null)

    const borderAccent = type === 'inc' ? 'border-l-2 border-l-emerald-400' : 'border-l-2 border-l-red-400'

    return (
        <div className={`mt-2 pl-2.5 ${borderAccent}`}>
            <p className={`text-[10px] font-bold uppercase tracking-wide mb-1 flex items-center gap-1.5 ${color}`}>
                {label}
                {ids.length > 0 && <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${pillClass}`}>{ids.length}</span>}
            </p>

            {/* Selected pills */}
            <div className="flex flex-wrap gap-1 mb-2">
                {ids.length === 0 && (
                    <span className="text-[11px] text-gray-400 italic">{t('admin.catalog.none_assigned')}</span>
                )}
                {ids.map(id => {
                    const sub = subServiceMap[id]
                    return (
                        <span key={id} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${pillClass}`}>
                            <span className="max-w-[240px] truncate">{sub ? sub.text : `[unknown: ${id.slice(0,8)}…]`}</span>
                            <button onClick={() => remove(id)} className="text-gray-400 hover:text-red-500 ml-0.5 shrink-0">
                                <FaTimesCircle size={10} />
                            </button>
                        </span>
                    )
                })}
            </div>

            {/* Search / create input */}
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={e => { setInput(e.target.value); computeDropPos(); setShowDrop(true) }}
                    onFocus={() => { computeDropPos(); setShowDrop(true) }}
                    onBlur={() => setTimeout(() => setShowDrop(false), 200)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            if (suggestions.length > 0) { select(suggestions[0].id) }
                        }
                        if (e.key === 'Escape') { setShowDrop(false); setInput('') }
                    }}
                    placeholder={t('admin.catalog.search_sub_services', { type: label.toLowerCase() })}
                    className="px-2.5 py-1.5 w-full text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40"
                />
                {showDrop && input.trim().length > 0 && dropPos && (
                    <div style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width }} className="z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto">
                        {suggestions.map(s => (
                            <button
                                key={s.id}
                                onMouseDown={() => select(s.id)}
                                className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-primary/5 flex items-center gap-2"
                            >
                                {s.cat && <span className="text-[9px] font-bold text-gray-400 uppercase shrink-0">{s.cat}</span>}
                                <span className="truncate">{s.text}</span>
                            </button>
                        ))}
                        {suggestions.length === 0 && (
                            <div className="px-3 py-2 text-xs italic text-gray-400">
                                {t('admin.catalog.no_sub_results')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

// ── ItemRow ───────────────────────────────────────────────────────────────────
function ItemRow({ item, isExpanded, onToggle, canEdit, onUpsert, onReset, onRemove, onReload,
                   subServices, subServiceMap, categories, createCategory,
                   priceGroups, priceOverrides, onUpsertOverrides, onDuplicate, subcatsByCategory }) {
    const col = getCatColor(categories, item.cat)
    const isModified = isItemModified(item)

    const [editName,    setEditName]    = useState(item.name)
    const [editCat,     setEditCat]     = useState(item.cat)
    const [editPrice,   setEditPrice]   = useState(String(item.price))
    const [editCost,    setEditCost]    = useState(String(item.cost))
    const [editSubcat,  setEditSubcat]  = useState(item.subcat || '')
    const [editUnit,    setEditUnit]    = useState(item.unit    || 'Unit')
    const [showNewCat,  setShowNewCat]  = useState(false)
    const [newCatName,  setNewCatName]  = useState('')
    const [newCatColor, setNewCatColor] = useState('amber')
    const [creatingCat, setCreatingCat] = useState(false)
    const { t } = useTranslation()
    const [saving,      setSaving]      = useState(false)
    const [saveErr,     setSaveErr]     = useState(null)
    const [subSaveErr,  setSubSaveErr]  = useState(null)

    const save = async (patch = {}) => {
        const p = parseFloat(editPrice)
        const c = parseFloat(editCost)
        if (isNaN(p) || isNaN(c)) return
        setSaving(true)
        setSaveErr(null)
        try {
            await onUpsert({ ...item, name: editName.trim() || item.name, cat: editCat, price: p, cost: c, unit: editUnit, subcat: editSubcat || null, ...patch })
        } catch (e) {
            setSaveErr(t('admin.catalog.save_failed'))
            onReload()
        } finally {
            setSaving(false)
        }
    }

    const handleCreateCategoryInRow = async () => {
        const trimmed = newCatName.trim().toUpperCase()
        if (!trimmed) return
        setCreatingCat(true)
        try {
            const created = await createCategory({ name: trimmed, color: newCatColor })
            setEditCat(created.name)
            setShowNewCat(false)
            setNewCatName('')
        } catch (e) {
            console.error(e)
        } finally {
            setCreatingCat(false)
        }
    }

    const handleIdsChange = async (type, ids, newSub) => {
        setSubSaveErr(null)
        const texts = ids.map(id => {
            if (newSub && id === newSub.id) return newSub.text
            return subServiceMap[id]?.text || ''
        })
        try {
            if (type === 'inc') {
                await onUpsert({ ...item, inc_ids: ids, inc: texts })
            } else {
                await onUpsert({ ...item, exc_ids: ids, exc: texts })
            }
        } catch {
            setSubSaveErr(t('admin.catalog.sub_save_failed'))
            onReload()
        }
    }

    const margin = item.price > 0 ? ((item.price - item.cost) / item.price * 100).toFixed(1) : 0
    const marginColor = margin >= 35 ? 'text-emerald-600' : 'text-amber-600'

    return (
        <div className={`border-b border-gray-100 first:rounded-t-xl last:rounded-b-xl last:border-b-0 ${isExpanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'} transition-colors`}>
            {/* Row header */}
            <div
                className={`flex items-center gap-2 px-3 py-2.5 ${canEdit ? 'cursor-pointer select-none' : ''}`}
                onClick={canEdit ? onToggle : undefined}
            >
                <div className={`w-2 h-2 rounded-full shrink-0 ${col.dot}`} />
                <div className="flex-1 min-w-0">
                    <p className="text-sm leading-snug text-gray-800 truncate">{item.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${col.badge}`}>{item.cat}</span>
                        {item.subcat && <span className="text-[10px] text-gray-500 font-medium">{item.subcat}</span>}
                        <span className="text-[10px] text-gray-400">{item.unit}</span>
                        {isModified && <span className="text-[10px] text-violet-500 font-semibold">● {t('admin.catalog.badge_edited')}</span>}
                    </div>
                </div>
                <div className="hidden gap-4 items-center text-right sm:flex shrink-0">
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide">{t('admin.catalog.col_price')}</p>
                        <p className="text-sm font-bold text-gray-800">${fmt(item.price)}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide">{t('admin.catalog.col_cost')}</p>
                        <p className="text-sm font-bold text-gray-500">${fmt(item.cost)}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide">{t('admin.catalog.col_margin')}</p>
                        <p className={`text-sm font-bold ${marginColor}`}>{margin}%</p>
                    </div>
                </div>
                {canEdit && (
                    <div className="ml-2 p-1.5 rounded-lg text-gray-400 shrink-0 pointer-events-none">
                        {isExpanded ? <FaChevronUp size={11} /> : <FaEdit size={11} />}
                    </div>
                )}
            </div>

            {/* Expanded editor */}
            {isExpanded && canEdit && (
                <div className="px-4 pt-1 pb-4 border-t border-gray-100">
                    {/* Name + Category */}
                    <div className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-2">
                        <div>
                            <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-1">{t('admin.catalog.label_name')}</label>
                            <input
                                type="text"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                className="px-2.5 py-1.5 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-1">{t('admin.catalog.label_category')}</label>
                            <select
                                value={showNewCat ? '__new__' : editCat}
                                onChange={e => {
                                    if (e.target.value === '__new__') { setShowNewCat(true) }
                                    else { setEditCat(e.target.value); setShowNewCat(false) }
                                }}
                                className="px-2.5 py-1.5 w-full text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            >
                                {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                <option value="__new__">{t('admin.catalog.new_category_option')}</option>
                            </select>
                            {showNewCat && (
                                <div className="p-3 mt-2 space-y-2 rounded-lg border border-primary/20 bg-primary/5">
                                    <input
                                        type="text"
                                        value={newCatName}
                                        onChange={e => setNewCatName(e.target.value.toUpperCase())}
                                        placeholder={t('admin.catalog.placeholder_cat_name')}
                                        className="px-2.5 py-1.5 w-full text-xs font-bold tracking-wide rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40"
                                    />
                                    <div className="flex gap-2 items-center">
                                        <span className="text-[10px] text-gray-400">{t('admin.catalog.label_color')}</span>
                                        {CAT_COLOR_OPTIONS.map(c => (
                                            <button key={c} type="button" onClick={e => { e.stopPropagation(); setNewCatColor(c) }}
                                                className={`w-5 h-5 rounded-full border-2 transition-all ${COLOR_CLASSES[c]?.dot ?? 'bg-gray-400'} ${newCatColor === c ? 'border-gray-700 scale-125' : 'border-transparent opacity-50 hover:opacity-100'}`}
                                            />
                                        ))}
                                    </div>
                                    <div className="flex gap-1.5">
                                        <button type="button"
                                            disabled={!newCatName.trim() || creatingCat}
                                            onClick={e => { e.stopPropagation(); handleCreateCategoryInRow() }}
                                            className="flex-1 py-1.5 bg-primary text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-all"
                                        >{creatingCat ? t('admin.catalog.creating') : t('admin.catalog.create')}</button>
                                        <button type="button"
                                            onClick={e => { e.stopPropagation(); setShowNewCat(false); setNewCatName('') }}
                                            className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-200 transition-all"
                                        >{t('admin.catalog.cancel')}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                {/* Unit, Price, Cost, Subcat */}
                    <div className="grid grid-cols-2 gap-3 mb-3 sm:grid-cols-5">
                        <div>
                            <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-1">{t('admin.catalog.label_unit')}</label>
                            <input
                                list="catalog-units-dl"
                                value={editUnit}
                                onChange={e => setEditUnit(e.target.value)}
                                className="px-2.5 py-1.5 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                placeholder="e.g. ft²"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-1">{t('admin.catalog.label_unit_price')}</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editPrice}
                                onChange={e => setEditPrice(e.target.value)}
                                className="px-2.5 py-1.5 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-1">{t('admin.catalog.label_cost')}</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editCost}
                                onChange={e => setEditCost(e.target.value)}
                                className="px-2.5 py-1.5 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] text-gray-500 uppercase tracking-wide mb-1">{t('admin.catalog.label_subcategory')}</label>
                            <input
                                list={`catalog-subcats-${item.id}`}
                                value={editSubcat}
                                onChange={e => setEditSubcat(e.target.value)}
                                placeholder={t('admin.catalog.placeholder_subcat')}
                                className="px-2.5 py-1.5 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                            <datalist id={`catalog-subcats-${item.id}`}>
                                {(subcatsByCategory[editCat] || []).map(s => <option key={s} value={s} />)}
                            </datalist>
                        </div>
                        <div className="flex flex-wrap gap-2 items-end">
                            <button
                                onClick={() => save()}
                                disabled={saving}
                                className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all"
                            >
                                <FaSave size={10} /> {saving ? t('admin.catalog.saving') : t('admin.catalog.save')}
                            </button>
                            {isModified && (
                                <button
                                    onClick={async () => {
                                        const orig = CATALOG_MAP[item.id]
                                        if (!orig) return
                                        await onReset(orig)
                                        setEditName(orig.name)
                                        setEditCat(orig.cat)
                                        setEditPrice(String(orig.price))
                                        setEditCost(String(orig.cost))
                                        setEditSubcat(orig.subcat || '')
                                        setEditUnit(orig.unit || 'Unit')
                                        setShowNewCat(false)
                                    }}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-200 transition-all"
                                >
                                    {t('admin.catalog.reset')}
                                </button>
                            )}
                            <button
                                onClick={() => onDuplicate && onDuplicate(item)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 text-gray-500 text-xs font-bold rounded-lg hover:bg-gray-100 transition-all border border-gray-200"
                                title={t('admin.catalog.duplicate')}
                            >
                                <FaCopy size={9} /> {t('admin.catalog.duplicate')}
                            </button>
                            {item.is_custom && (
                                <button
                                    onClick={async () => { try { await onRemove(item.id) } catch { onReload() } }}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-500 text-xs font-bold rounded-lg hover:bg-red-100 transition-all"
                                >
                                    <FaTrash size={9} /> {t('admin.catalog.delete')}
                                </button>
                            )}
                            {saveErr    && <span className="text-[10px] text-red-500">{saveErr}</span>}
                            {subSaveErr && <span className="text-[10px] text-red-500">{subSaveErr}</span>}
                        </div>
                    </div>

                    {/* Sub-service pickers */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <SubServicePicker
                            ids={item.inc_ids || []}
                            type="inc"
                            itemCat={item.cat}
                            subServices={subServices}
                            subServiceMap={subServiceMap}
                            onIdsChange={(ids, newSub) => handleIdsChange('inc', ids, newSub)}
                            oppositeIds={item.exc_ids || []}
                        />
                        <SubServicePicker
                            ids={item.exc_ids || []}
                            type="exc"
                            itemCat={item.cat}
                            subServices={subServices}
                            subServiceMap={subServiceMap}
                            onIdsChange={(ids, newSub) => handleIdsChange('exc', ids, newSub)}
                            oppositeIds={item.inc_ids || []}
                        />
                    </div>

                    {/* Price overrides per group */}
                    {priceGroups && priceGroups.length > 0 && (
                        <PriceOverridesSection
                            itemId={item.id}
                            defaultPrice={item.price}
                            defaultCost={item.cost}
                            groups={priceGroups}
                            overrides={priceOverrides || []}
                            onSave={onUpsertOverrides}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

// ── PriceOverridesSection ────────────────────────────────────────────────────
function PriceOverridesSection({ itemId, defaultPrice, defaultCost, groups, overrides, onSave }) {
    const { t } = useTranslation()
    const buildRows = () =>
        groups.map(g => {
            const ov = overrides.find(o => o.group_id === g.id)
            return { group_id: g.id, name: g.name, price: ov ? String(ov.price) : '', cost: ov ? String(ov.cost) : '' }
        })
    const [rows,    setRows]    = useState(buildRows)
    const [saving,  setSaving]  = useState(false)
    const [saved,   setSaved]   = useState(false)
    const [saveErr, setSaveErr] = useState(null)

    // Re-sync whenever async overrides or groups arrive / change
    useEffect(() => {
        setRows(buildRows())
        setSaved(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overrides, groups])

    const update = (idx, field, val) => {
        setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r))
        setSaved(false)
        setSaveErr(null)
    }

    const clearRow = (idx) => {
        setRows(prev => prev.map((r, i) => i === idx ? { ...r, price: '', cost: '' } : r))
        setSaved(false)
        setSaveErr(null)
    }

    const handleSave = async () => {
        setSaving(true)
        setSaveErr(null)
        try {
            const payload = rows
                .filter(r => r.price !== '' && r.cost !== '')
                .map(r => ({ group_id: r.group_id, price: parseFloat(r.price) || 0, cost: parseFloat(r.cost) || 0 }))
            await onSave(itemId, payload)
            setSaved(true)
        } catch (err) { setSaveErr(err.message) }
        finally { setSaving(false) }
    }

    return (
        <div className="mt-3 p-3 bg-blue-50/60 border border-blue-100 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
                <FaMoneyBillWave className="text-blue-500" size={12} />
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wide">{t('admin.catalog.price_overrides')}</span>
            </div>
            <div className="space-y-1">
                {/* Column headers */}
                <div className="flex items-center gap-2 text-[10px] text-gray-400 uppercase tracking-wide font-semibold">
                    <span className="w-28 shrink-0">{t('admin.catalog.label_category')}</span>
                    <span className="flex-1 text-center">{t('admin.catalog.price_col_price')}</span>
                    <span className="flex-1 text-center">{t('admin.catalog.price_col_cost')}</span>
                    <span className="w-12" />
                </div>
                {/* Default row (read-only) */}
                <div className="flex items-center gap-2 text-xs">
                    <span className="w-28 text-gray-500 font-semibold shrink-0">{t('admin.catalog.price_default')}</span>
                    <span className="flex-1 px-2 py-1 bg-gray-100 rounded text-gray-500 text-center">${fmt(defaultPrice)}</span>
                    <span className="flex-1 px-2 py-1 bg-gray-100 rounded text-gray-500 text-center">${fmt(defaultCost)}</span>
                    <span className="w-12" />
                </div>
                {rows.map((row, idx) => (
                    <div key={row.group_id} className="flex items-center gap-2 text-xs">
                        <span className="w-28 text-gray-700 font-semibold shrink-0 truncate">{row.name}</span>
                        <input
                            type="number" step="0.01" min="0"
                            value={row.price}
                            onChange={e => update(idx, 'price', e.target.value)}
                            placeholder={String(defaultPrice)}
                            className="flex-1 px-2 py-1 rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40 text-center"
                        />
                        <input
                            type="number" step="0.01" min="0"
                            value={row.cost}
                            onChange={e => update(idx, 'cost', e.target.value)}
                            placeholder={String(defaultCost)}
                            className="flex-1 px-2 py-1 rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40 text-center"
                        />
                        <button
                            onClick={() => clearRow(idx)}
                            disabled={row.price === '' && row.cost === ''}
                            className="w-12 flex justify-center text-gray-300 hover:text-red-400 disabled:opacity-20 transition-colors"
                            title="Clear override (revert to default)"
                        >
                            <FaTimes size={10} />
                        </button>
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-2">
                {saveErr && <span className="text-[10px] text-red-500 flex-1">{saveErr}</span>}
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1 px-3 py-1 text-xs font-bold text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-all"
                >
                    <FaSave size={9} /> {saved ? t('admin.catalog.price_saved') : saving ? t('admin.catalog.saving') : t('admin.catalog.price_save_overrides')}
                </button>
            </div>
        </div>
    )
}

// ── AddItemModal ──────────────────────────────────────────────────────────────
const CAT_COLOR_OPTIONS = ['amber', 'blue', 'emerald', 'violet', 'rose', 'sky', 'orange', 'teal']

function AddItemModal({ onSave, onClose, categories, subServices, subServiceMap, createCategory, subcatsByCategory }) {
    const catNames = categories.map(c => c.name)
    const [name,        setName]        = useState('')
    const [cat,         setCat]         = useState(catNames[0] || 'CONCRETE')
    const [subcat,      setSubcat]      = useState('')
    const [unit,        setUnit]        = useState('ft²')
    const [price,       setPrice]       = useState('')
    const [cost,        setCost]        = useState('')
    const [incIds,      setIncIds]      = useState([])
    const [excIds,      setExcIds]      = useState([])
    const [showNewCat,  setShowNewCat]  = useState(false)
    const [newCatName,  setNewCatName]  = useState('')
    const [newCatColor, setNewCatColor] = useState('amber')
    const { t } = useTranslation()
    const [creatingCat, setCreatingCat] = useState(false)

    const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')

    const handleSave = () => {
        const p = parseFloat(price)
        const c = parseFloat(cost)
        if (!name.trim() || isNaN(p) || isNaN(c)) return
        const incTexts = incIds.map(id => subServiceMap[id]?.text || '')
        const excTexts = excIds.map(id => subServiceMap[id]?.text || '')
        onSave({
            id:      `custom_${slug}_${Date.now()}`,
            cat,
            subcat:  subcat.trim() || null,
            name:    name.trim(),
            unit,
            price:   p,
            cost:    c,
            inc:     incTexts,
            exc:     excTexts,
            inc_ids: incIds,
            exc_ids: excIds,
        })
        onClose()
    }

    const handleCreateCategory = async () => {
        const trimmed = newCatName.trim().toUpperCase()
        if (!trimmed) return
        setCreatingCat(true)
        try {
            const created = await createCategory({ name: trimmed, color: newCatColor })
            setCat(created.name)
            setShowNewCat(false)
            setNewCatName('')
        } catch (e) {
            console.error(e)
        } finally {
            setCreatingCat(false)
        }
    }

    return (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
                <div className="flex justify-between items-center mb-5">
                    <h3 className="text-lg font-bold text-gray-900">{t('admin.catalog.modal_title')}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500"><FaTimesCircle size={18} /></button>
                </div>
                <div className="pb-52 space-y-3">
                    <div>
                        <label className="block mb-1 text-xs text-gray-500">{t('admin.catalog.label_name')}</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)}
                            className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            placeholder={t('admin.catalog.placeholder_name')} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block mb-1 text-xs text-gray-500">{t('admin.catalog.label_category')}</label>
                            <select
                                value={showNewCat ? '__new__' : cat}
                                onChange={e => {
                                    if (e.target.value === '__new__') { setShowNewCat(true) }
                                    else { setCat(e.target.value); setShowNewCat(false) }
                                }}
                                className="px-3 py-2 w-full text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40">
                                {catNames.map(cn => <option key={cn} value={cn}>{cn}</option>)}
                                <option value="__new__">{t('admin.catalog.new_category_option')}</option>
                            </select>
                            {showNewCat && (
                                <div className="p-3 mt-2 space-y-2 rounded-lg border border-primary/20 bg-primary/5">
                                    <input
                                        type="text"
                                        value={newCatName}
                                        onChange={e => setNewCatName(e.target.value.toUpperCase())}
                                        placeholder={t('admin.catalog.placeholder_cat_name')}
                                        className="px-2.5 py-1.5 w-full text-xs font-bold tracking-wide rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/40"
                                    />
                                    <div className="flex gap-2 items-center">
                                        <span className="text-[10px] text-gray-400">{t('admin.catalog.label_color')}</span>
                                        {CAT_COLOR_OPTIONS.map(c => (
                                            <button key={c} type="button" onClick={() => setNewCatColor(c)}
                                                className={`w-5 h-5 rounded-full border-2 transition-all ${COLOR_CLASSES[c]?.dot ?? 'bg-gray-400'} ${newCatColor === c ? 'border-gray-700 scale-125' : 'border-transparent opacity-50 hover:opacity-100'}`}
                                            />
                                        ))}
                                    </div>
                                    <div className="flex gap-1.5">
                                        <button type="button"
                                            disabled={!newCatName.trim() || creatingCat}
                                            onClick={handleCreateCategory}
                                            className="flex-1 py-1.5 bg-primary text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-all"
                                        >{creatingCat ? t('admin.catalog.creating') : t('admin.catalog.create')}</button>
                                        <button type="button"
                                            onClick={() => { setShowNewCat(false); setNewCatName('') }}
                                            className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-200 transition-all"
                                        >{t('admin.catalog.cancel')}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block mb-1 text-xs text-gray-500">{t('admin.catalog.label_subcat_optional')}</label>
                            <input
                                list="catalog-subcats-modal"
                                value={subcat}
                                onChange={e => setSubcat(e.target.value)}
                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                placeholder={t('admin.catalog.placeholder_subcat')}
                            />
                            <datalist id="catalog-subcats-modal">
                                {(subcatsByCategory[cat] || []).map(s => <option key={s} value={s} />)}
                            </datalist>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="block mb-1 text-xs text-gray-500">{t('admin.catalog.label_unit')}</label>
                            <input
                                list="catalog-units-dl"
                                value={unit}
                                onChange={e => setUnit(e.target.value)}
                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                placeholder="e.g. ft²"
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs text-gray-500">{t('admin.catalog.label_unit_price')}</label>
                            <input type="number" step="0.01" min="0" value={price} onChange={e => setPrice(e.target.value)}
                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                placeholder="0.00" />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs text-gray-500">{t('admin.catalog.label_cost')}</label>
                            <input type="number" step="0.01" min="0" value={cost} onChange={e => setCost(e.target.value)}
                                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                placeholder="0.00" />
                        </div>
                    </div>
                    <SubServicePicker ids={incIds} type="inc" itemCat={cat}
                        subServices={subServices} subServiceMap={subServiceMap}
                        onIdsChange={setIncIds} oppositeIds={excIds} />
                    <SubServicePicker ids={excIds} type="exc" itemCat={cat}
                        subServices={subServices} subServiceMap={subServiceMap}
                        onIdsChange={setExcIds} oppositeIds={incIds} />
                </div>
                <div className="flex gap-2 px-0 pt-4">
                    <button onClick={handleSave} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary text-white text-sm font-bold rounded-xl hover:bg-primary/90 transition-all">
                        <FaCheckCircle size={12} /> {t('admin.catalog.add_to_catalog')}
                    </button>
                    <button onClick={onClose} className="px-4 py-2.5 bg-gray-100 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-200 transition-all">{t('admin.catalog.cancel')}</button>
                </div>
            </div>
        </div>
    )
}

// ── MAIN CATALOG EDITOR TAB ───────────────────────────────────────────────────
export default function CatalogEditorTab({ canEdit, session }) {
    const { t } = useTranslation()
    const { items, loading, error, upsertItem, removeItem, renameCatItems, reload } = useCatalogItems(session)
    const { groups: priceGroups, overrides: priceOverrides, upsertOverrides } = usePriceGroups()
    const { units } = useUnits()
    const { items: subServices }                                    = useSubServices(session)
    const { categories, createCategory, updateCategory }           = useCategories()
    const subServiceMap = useMemo(() => buildSubServiceMap(subServices), [subServices])
    const subcatsByCategory = useMemo(() => {
        const map = {}
        items.forEach(i => {
            if (i.subcat && i.cat) {
                if (!map[i.cat]) map[i.cat] = new Set()
                map[i.cat].add(i.subcat)
            }
        })
        return Object.fromEntries(Object.entries(map).map(([k, v]) => [k, [...v].sort()]))
    }, [items])

    const [search, setSearch] = useState('')
    const [catFilter, setCatFilter] = useState(null)
    const [expanded, setExpanded] = useState({})
    const [showAddModal, setShowAddModal] = useState(false)
    const [showExportModal, setShowExportModal] = useState(false)
    const [editingCatId,   setEditingCatId]   = useState(null)
    const [editingCatName, setEditingCatName] = useState('')
    const [renameSaving,   setRenameSaving]   = useState(false)

    const filtered = useMemo(() => {
        const q = search.trim()
        let regex
        try { regex = q ? new RegExp(q, 'i') : null } catch { regex = null }
        return items.filter(item => {
            const matchesCat = !catFilter || item.cat === catFilter
            const matchesSearch = !q || (regex ? regex.test(item.name) : item.name.toLowerCase().includes(q.toLowerCase()))
            return matchesCat && matchesSearch
        })
    }, [items, search, catFilter])

    const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }))

    const handleRenameCategory = async (cat) => {
        const newName = editingCatName.trim().toUpperCase()
        if (!newName || newName === cat.name) { setEditingCatId(null); return }
        setRenameSaving(true)
        try {
            await updateCategory(cat.id, { name: newName })
            await renameCatItems(cat.name, newName)
            if (catFilter === cat.name) setCatFilter(newName)
            setEditingCatId(null)
        } catch (e) {
            console.error('rename category failed', e)
        } finally {
            setRenameSaving(false)
        }
    }

    const handleAddItem = async (item) => {
        await upsertItem({ ...item, is_custom: true })
        setShowAddModal(false)
    }

    const handleDuplicateItem = async (item) => {
        const slug = item.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')
        const newId = `custom_${slug}_${Date.now()}`
        await upsertItem({
            ...item,
            id: newId,
            name: t('admin.catalog.copy_of', { name: item.name }),
            is_custom: true,
        })
    }

    const modifiedCount = useMemo(() => items.filter(isItemModified).length, [items])

    if (loading) {
        return (
            <div className="flex gap-3 justify-center items-center py-24 text-gray-400">
                <FaSync size={16} className="animate-spin" />
                <span className="text-sm">{t('admin.catalog.loading')}</span>
            </div>
        )
    }

    if (!canEdit) {
        return (
            <div className="flex flex-col justify-center items-center py-24 text-center">
                <div className="p-5 mb-4 bg-gray-100 rounded-2xl">
                    <FaLock size={28} className="text-gray-400" />
                </div>
                <h2 className="mb-1 text-base font-bold text-gray-700">{t('admin.catalog.restricted_title')}</h2>
                <p className="max-w-sm text-sm text-gray-400">{t('admin.catalog.restricted_body')}</p>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-base font-bold text-gray-800">{t('admin.catalog.title')}</h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                        {t('admin.catalog.items_total', { count: items.length })} · {modifiedCount > 0 ? <span className="font-semibold text-violet-500">{t('admin.catalog.edited_count', { count: modifiedCount })}</span> : t('admin.catalog.no_edits')}
                        <span className="ml-2 text-[10px] text-gray-300">{t('admin.catalog.changes_saved')}</span>
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={reload}
                        className="flex items-center gap-1.5 px-3 py-2 text-gray-500 border border-gray-200 text-sm font-medium rounded-xl hover:bg-gray-50 transition-all"
                        title={t('admin.catalog.reload')}
                    >
                        <FaSync size={11} />
                    </button>
                    <button
                        onClick={() => setShowExportModal(true)}
                        className="flex items-center gap-1.5 px-3 py-2 text-primary border border-primary/30 text-sm font-medium rounded-xl hover:bg-primary/5 transition-all"
                        title={t('admin.catalog.export_excel')}
                    >
                        <FaDownload size={11} /> {t('admin.catalog.export')}
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-white rounded-xl shadow-sm transition-all bg-primary hover:bg-primary/90"
                    >
                        <FaPlus size={11} /> {t('admin.catalog.new_item')}
                    </button>
                </div>
            </div>

            {/* Search + Filter */}
            <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2" size={12} />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('admin.catalog.search_placeholder')}
                        className="py-2 pr-3 pl-8 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                </div>
                <div className="flex flex-wrap gap-1">
                    <button onClick={() => setCatFilter(null)} className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${!catFilter ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>{t('admin.catalog.filter_all')}</button>
                    {categories.map(c => {
                        const col = getCatColor(categories, c.name)
                        if (editingCatId === c.id) {
                            return (
                                <div key={c.id} className="flex gap-1 items-center">
                                    <input
                                        autoFocus
                                        value={editingCatName}
                                        onChange={e => setEditingCatName(e.target.value.toUpperCase())}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleRenameCategory(c)
                                            if (e.key === 'Escape') setEditingCatId(null)
                                        }}
                                        disabled={renameSaving}
                                        className="px-2 py-1 text-[10px] font-bold tracking-wide rounded-lg border border-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/40 w-28 disabled:opacity-50"
                                    />
                                    <button onClick={() => handleRenameCategory(c)} disabled={renameSaving} className="p-1 text-emerald-600 transition-colors hover:text-emerald-700 disabled:opacity-50">
                                        <FaCheckCircle size={12} />
                                    </button>
                                    <button onClick={() => setEditingCatId(null)} className="p-1 text-gray-400 transition-colors hover:text-red-500">
                                        <FaTimesCircle size={12} />
                                    </button>
                                </div>
                            )
                        }
                        return (
                            <div key={c.id} className="flex relative items-center group">
                                <button
                                    onClick={() => setCatFilter(catFilter === c.name ? null : c.name)}
                                    className={`pr-6 pl-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${catFilter === c.name ? col.badge + ' shadow-sm' : 'bg-white text-gray-400 border-gray-100 hover:bg-gray-50'}`}
                                >{c.name}</button>
                                {canEdit && (
                                    <button
                                        onClick={e => { e.stopPropagation(); setEditingCatId(c.id); setEditingCatName(c.name) }}
                                        className="absolute right-1 top-1/2 text-gray-400 opacity-0 transition-all -translate-y-1/2 group-hover:opacity-100 hover:text-primary"
                                        title="Rename category"
                                    >
                                        <FaEdit size={9} />
                                    </button>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* DB error banner */}
            {error && (
                <p className="px-3 py-2 text-xs text-amber-600 bg-amber-50 rounded-lg border border-amber-200">
                    {t('admin.catalog.db_error', { error })}
                </p>
            )}

            {/* Results count */}
            {search && (
                <p className="text-xs text-gray-400">{t('admin.catalog.results_for', { count: filtered.length, query: search })}</p>
            )}

            <datalist id="catalog-units-dl">
                {units.map(u => <option key={u} value={u} />)}
            </datalist>

            {/* Item list */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                {filtered.length === 0 ? (
                    <div className="py-12 text-sm text-center text-gray-400">{t('admin.catalog.no_match')}</div>
                ) : (
                    filtered.map(item => (
                        <ItemRow
                            key={item.id}
                            item={item}
                            isExpanded={!!expanded[item.id]}
                            onToggle={() => toggle(item.id)}
                            canEdit={canEdit}
                            onUpsert={upsertItem}
                            onReset={upsertItem}
                            onRemove={removeItem}
                            onReload={reload}
                            subServices={subServices}
                            subServiceMap={subServiceMap}
                            categories={categories}
                            createCategory={createCategory}
                            priceGroups={priceGroups}
                            priceOverrides={priceOverrides.filter(o => o.item_id === item.id)}
                            onUpsertOverrides={upsertOverrides}
                            onDuplicate={handleDuplicateItem}
                            subcatsByCategory={subcatsByCategory}
                        />
                    ))
                )}
            </div>

            {showAddModal && (
                <AddItemModal
                    onSave={handleAddItem}
                    onClose={() => setShowAddModal(false)}
                    categories={categories}
                    subServices={subServices}
                    subServiceMap={subServiceMap}
                    createCategory={createCategory}
                    subcatsByCategory={subcatsByCategory}
                />
            )}

            {showExportModal && (
                <ExportCatalogModal
                    items={items}
                    onClose={() => setShowExportModal(false)}
                />
            )}
        </div>
    )
}

// ── ExportCatalogModal ────────────────────────────────────────────────────────
function ExportCatalogModal({ items, onClose }) {
    const { t } = useTranslation()
    const hasItems = items.length > 0

    const previewItems = useMemo(() => items.slice(0, 2), [items])

    const handleExport = (format) => {
        if (!hasItems) return
        let wb
        if (format === 'two_sheet') wb = exportTwoSheet(items)
        else if (format === 'flat') wb = exportFlatSheet(items)
        else if (format === 'horizontal') wb = exportHorizontalSheet(items)
        if (wb) {
            downloadWorkbook(wb, getExportFilename())
            onClose()
        }
    }

    const trunc = (text, len = 40) => {
        if (!text) return ''
        return text.length > len ? text.slice(0, len) + '…' : text
    }

    const optionCards = [
        {
            key: 'two_sheet',
            title: t('admin.catalog.export_option_two_sheet'),
            desc: t('admin.catalog.export_option_two_sheet_desc'),
            previewCols: ['ID', 'Category', 'Name', 'Price', 'Cost', 'Unit'],
            previewRows: previewItems.map(item => [
                trunc(item.id, 20),
                item.cat || '',
                trunc(item.name, 25),
                item.price,
                item.cost,
                item.unit || '',
            ]),
        },
        {
            key: 'flat',
            title: t('admin.catalog.export_option_flat'),
            desc: t('admin.catalog.export_option_flat_desc'),
            previewCols: ['ID', 'Name', 'Inclusions', 'Exclusions'],
            previewRows: previewItems.map(item => [
                trunc(item.id, 20),
                trunc(item.name, 25),
                trunc(Array.isArray(item.inc) && item.inc[0] ? item.inc[0] : '', 30),
                trunc(Array.isArray(item.exc) && item.exc[0] ? item.exc[0] : '', 30),
            ]),
        },
        {
            key: 'horizontal',
            title: t('admin.catalog.export_option_horizontal'),
            desc: t('admin.catalog.export_option_horizontal_desc'),
            previewCols: ['ID', 'Name', 'INC_1', 'INC_2', 'EXC_1'],
            previewRows: previewItems.map(item => [
                trunc(item.id, 20),
                trunc(item.name, 25),
                trunc(Array.isArray(item.inc) && item.inc[0] ? item.inc[0] : '', 20),
                trunc(Array.isArray(item.inc) && item.inc[1] ? item.inc[1] : '', 20),
                trunc(Array.isArray(item.exc) && item.exc[0] ? item.exc[0] : '', 20),
            ]),
        },
    ]

    return (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
                <div className="flex justify-between items-center mb-5">
                    <h3 className="text-lg font-bold text-gray-900">{t('admin.catalog.export_modal_title')}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500"><FaTimesCircle size={18} /></button>
                </div>

                {!hasItems && (
                    <div className="py-8 text-center text-sm text-gray-400">
                        {t('admin.catalog.export_no_items')}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {optionCards.map(card => (
                        <button
                            key={card.key}
                            onClick={() => handleExport(card.key)}
                            disabled={!hasItems}
                            className={`text-left p-4 rounded-xl border transition-all ${
                                hasItems
                                    ? 'border-gray-200 hover:border-primary/40 hover:shadow-lg bg-white'
                                    : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                            }`}
                        >
                            <h4 className="text-sm font-bold text-gray-800 mb-1">{card.title}</h4>
                            <p className="text-[11px] text-gray-400 mb-3 leading-relaxed">{card.desc}</p>

                            {/* Live mini-table preview */}
                            <div className="mb-3 overflow-hidden rounded border border-gray-100">
                                <table className="w-full text-[9px] border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            {card.previewCols.map(col => (
                                                <th key={col} className="px-1.5 py-1 text-left font-bold text-gray-500 border-b border-gray-100 truncate">{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {card.previewRows.map((row, ri) => (
                                            <tr key={ri} className={ri % 2 === 1 ? 'bg-gray-50/50' : ''}>
                                                {row.map((cell, ci) => (
                                                    <td key={ci} className="px-1.5 py-1 text-gray-600 border-b border-gray-50 truncate max-w-[80px]">{cell}</td>
                                                ))}
                                            </tr>
                                        ))}
                                        {card.previewRows.length === 0 && (
                                            <tr>
                                                <td colSpan={card.previewCols.length} className="px-1.5 py-1 text-gray-300 italic">—</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex items-center gap-1.5 text-[11px] font-bold text-primary">
                                <FaDownload size={10} />
                                {t('admin.catalog.export_download')}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    )
}
